import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { articlesAPI, categoriesAPI } from '../utils/api';
import { ArticleCard } from '../components/ArticleCard';
import { Sidebar } from '../components/Sidebar';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { SEO } from '../components/SEO';
import { useRegion } from '../context/RegionContext';

// Default banner images for categories (fallback)
const DEFAULT_BANNERS = {
  'deals-offers': 'https://images.unsplash.com/photo-1607082350899-7e105aa886ae?w=1600&q=80',
  'coupon-guides': 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=1600&q=80',
  'shopping-tips': 'https://images.unsplash.com/photo-1483985988355-763728e1935b?w=1600&q=80',
  'best-deals-today': 'https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?w=1600&q=80',
  'festival-sales': 'https://images.unsplash.com/photo-1513885535751-8b9238bd345a?w=1600&q=80',
  'brand-discounts': 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=1600&q=80',
  'buying-guides': 'https://images.unsplash.com/photo-1472851294608-062f824d29cc?w=1600&q=80',
  'default': 'https://images.unsplash.com/photo-1556742044-3c52d6e88c62?w=1600&q=80'
};

export const CategoryPage = () => {
  const { slug } = useParams();
  const { currentRegion, region } = useRegion();
  const [category, setCategory] = useState(null);
  const [articles, setArticles] = useState([]);
  const [trendingArticles, setTrendingArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const categoriesRes = await categoriesAPI.getAll();
        const foundCategory = categoriesRes.data.find(cat => cat.slug === slug);
        
        if (foundCategory) {
          setCategory(foundCategory);
          const articlesRes = await articlesAPI.getAll({ 
            category_id: foundCategory.id, 
            is_published: true,
            region: currentRegion !== 'global' ? currentRegion : undefined,
            limit: 50
          });
          setArticles(articlesRes.data);
        }

        const allArticlesRes = await articlesAPI.getAll({ 
          is_published: true, 
          region: currentRegion !== 'global' ? currentRegion : undefined,
          limit: 20 
        });
        setTrendingArticles(allArticlesRes.data.sort((a, b) => b.views - a.views).slice(0, 5));
      } catch (error) {
        console.error('Failed to fetch category:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [slug, currentRegion]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-brand-red mx-auto mb-4"></div>
          <p className="text-gray-600 font-inter">Loading category...</p>
        </div>
      </div>
    );
  }

  if (!category) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-playfair font-bold text-brand-green mb-4">Category Not Found</h1>
        </div>
      </div>
    );
  }

  // Get banner image - use category's banner_image or fallback to default
  const bannerImage = category.banner_image || DEFAULT_BANNERS[category.slug] || DEFAULT_BANNERS['default'];

  // Build breadcrumbs for SEO schema
  const seoBreadcrumbs = [
    { name: 'Home', url: '/' },
    ...(currentRegion !== 'global' ? [{ name: region.name, url: `/${currentRegion}` }] : []),
    { name: category.name, url: `/category/${category.slug}` }
  ];

  return (
    <>
      <SEO 
        title={`${category.name} - Best Deals & Guides`}
        description={category.description || `Discover the best ${category.name.toLowerCase()} deals, coupons, and shopping guides. Save money with our expert tips and verified promo codes.`}
        type="website"
        breadcrumbs={seoBreadcrumbs}
        image={bannerImage}
        canonicalPath={currentRegion === 'global' ? `/category/${slug}` : `/${currentRegion}/category/${slug}`}
      />

      <div className="min-h-screen" data-testid="category-page">
        {/* Breadcrumbs */}
        <Breadcrumbs items={[]} currentPage={category.name} />

        {/* Hero Banner */}
        <div 
          className="relative min-h-[280px] md:min-h-[340px] flex items-center overflow-hidden"
          data-testid="category-hero"
        >
          {/* Background Image */}
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${bannerImage})` }}
          >
            {/* Dark Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 via-gray-900/75 to-gray-900/60"></div>
            {/* Bottom fade */}
            <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-gray-900/50 to-transparent"></div>
          </div>
          
          {/* Content */}
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16 w-full">
            <div className="max-w-2xl">
              <h1 
                className="text-4xl md:text-5xl lg:text-6xl font-playfair font-bold text-white mb-4 drop-shadow-lg" 
                data-testid="category-name"
              >
                {category.name}
              </h1>
              {category.description && (
                <p 
                  className="text-lg md:text-xl text-gray-200 leading-relaxed drop-shadow-md" 
                  data-testid="category-description"
                >
                  {category.description}
                </p>
              )}
              <div className="mt-6 flex items-center gap-4 text-gray-300">
                <span className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm">
                  {articles.length} {articles.length === 1 ? 'Article' : 'Articles'}
                </span>
                {currentRegion !== 'global' && (
                  <span className="inline-flex items-center gap-2 bg-brand-yellow/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm text-brand-yellow">
                    {region.flag} {region.name}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-8">
              {articles.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="articles-grid">
                  {articles.map((article) => (
                    <ArticleCard key={article.id} article={article} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-16 bg-gray-50 rounded-xl border border-gray-100" data-testid="no-articles">
                  <div className="text-6xl mb-4">📭</div>
                  <p className="text-gray-600 font-inter text-lg mb-2">No articles found in this category yet.</p>
                  <p className="text-gray-500 text-sm">
                    {currentRegion !== 'global' 
                      ? `Try switching to Global for more content.`
                      : `Check back soon for new content!`
                    }
                  </p>
                </div>
              )}
            </div>

            <div className="lg:col-span-4">
              <Sidebar trendingArticles={trendingArticles} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CategoryPage;
